import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { getAdminBuildInfo } from "./buildInfo";
import { shouldAutoReloadChunkFailureOnce } from "./chunkLoadRecovery";
import { generateCorrelationId } from "./logging/correlationId";
import { sanitizeAdminRouteForLogging } from "./logging/sanitizeAdminRouteForLogging";
import { reloadCurrentPage } from "./reloadCurrentPage";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "utils/registerViteChunkLoadRecovery",
);

const PRELOAD_ERROR_EVENT = "vite:preloadError";
let hasRegisteredPreloadHandler = false;

type VitePreloadErrorEvent = Event & {
  payload?: unknown;
  preventDefault?: () => void;
};

const getPreloadErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error) {
    return error.message;
  }

  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  return null;
};

/**
 * Handles Vite dynamic-import preload failures once before the app falls back to
 * the route-level chunk error boundary.
 *
 * @remarks
 * This matches Vite's recommended recovery pattern for version-skew chunk misses
 * while avoiding reload loops in the same session.
 *
 * @see docs/admin/README.md
 */
export const registerViteChunkLoadRecovery = (): void => {
  if (
    typeof window === "undefined" ||
    typeof window.addEventListener !== "function" ||
    hasRegisteredPreloadHandler
  ) {
    return;
  }

  const handler = (event: Event) => {
    const preloadEvent = event as VitePreloadErrorEvent;
    const correlationId = generateCorrelationId();
    const { buildVersion, buildSha } = getAdminBuildInfo();
    const shouldReload = shouldAutoReloadChunkFailureOnce();
    const route = sanitizeAdminRouteForLogging(window.location.pathname);

    logger
      .withCorrelationId(correlationId)
      .error(
        "Vite dynamic-import preload failed",
        preloadEvent.payload instanceof Error
          ? preloadEvent.payload
          : undefined,
        {
          feature: "FND",
          operation: "VITE_PRELOAD_ERROR",
          route,
          buildVersion,
          buildSha: buildSha ?? undefined,
          shouldReload,
          errorMessage:
            getPreloadErrorMessage(preloadEvent.payload) ?? undefined,
        },
      );

    if (!shouldReload) {
      return;
    }

    preloadEvent.preventDefault();
    reloadCurrentPage();
  };

  window.addEventListener(PRELOAD_ERROR_EVENT, handler);
  hasRegisteredPreloadHandler = true;
};
